import { Injectable } from '@angular/core';
import { TenantService } from '@vsolv/core/multi-tenant/web';
import { PortalService } from '@vsolv/packages/portal-config/web';

@Injectable({
  providedIn: 'root',
})
export class SalesService {
  private cachedSalesDisabled: boolean | null = null;
  private lastTenantId: string | null = null;
  private lastFetchTime = 0;
  private readonly CACHE_DURATION = 2 * 60 * 1000; // Reduced to 2 minutes for faster updates

  constructor(
    private tenantSvc: TenantService,
    private portalSvc: PortalService
  ) {}

  /**
   * Get salesDisabled status - only fetches when needed
   * Used by layout component (button) and SalesGuard (checkout protection)
   * Cache is shorter to ensure admin changes are reflected quickly
   */
  async getSalesDisabled(): Promise<boolean> {
    const tenantId = this.tenantSvc.tenantId;
    if (!tenantId) {
      return false;
    }

    // Check if we have valid cached data
    const now = Date.now();
    if (
      tenantId === this.lastTenantId &&
      this.cachedSalesDisabled !== null &&
      now - this.lastFetchTime < this.CACHE_DURATION
    ) {
      return this.cachedSalesDisabled;
    }

    try {
      // Fetch fresh data - bypass PortalService cache for critical setting
      const config = await this.portalSvc.getOne(tenantId);
      const salesDisabled = config?.salesDisabled || false;

      // Cache the result
      this.cachedSalesDisabled = salesDisabled;
      this.lastTenantId = tenantId;
      this.lastFetchTime = now;

      return salesDisabled;
    } catch (error) {
      console.warn('Failed to fetch sales disabled status:', error);
      return false; // Default to allowing sales on error
    }
  }

  /**
   * Clear cache - useful when admin updates settings
   * This ensures immediate reflection of changes
   */
  clearCache(): void {
    this.cachedSalesDisabled = null;
    this.lastTenantId = null;
    this.lastFetchTime = 0;
  }

  /**
   * Force refresh - bypasses cache completely
   * Use this when you need the absolute latest value
   */
  async forceRefresh(): Promise<boolean> {
    this.clearCache();
    return await this.getSalesDisabled();
  }
}
